import { Typography, Box, Divider, Grid, Alert, Button } from "@mui/material";
import {
	CalendarToday as CalendarIcon,
} from "@mui/icons-material";
import RefreshIcon from "@mui/icons-material/Refresh";
import { useTranslation } from "react-i18next";
import { fetchCompanyUsersRequest } from "../store/slices/database/companyUsersSlice";
import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from "react";
import LoadingBackdrop from "../components/common/custom-component/LoadingBackdrop";
import Loader from "../components/common/custom-component/Loader";
import DataContainer, { TableActionButton, TableActionGroup } from "../components/common/DataContainer";
import { useSearchFilter } from "../hooks/useSearchFilter";
import { useAuth } from "../hooks/useAuth";
import { useToast } from "../components/common/custom-component/ToastProvider";
import { useDispatch, useSelector } from "react-redux";
import { fetchCompanyRolesThunk, patchUserRolesThunk, getUserRolesThunk, addUserThunk, unassignUserThunk, editUserThunk } from "../store/thunks/mdsThunks";
import { logger } from "helpers/logger";
import EnhancedConfirmationDialog from "../components/common/custom-component/EnhancedConfirmationDialog";
import { manageUsersReducer, initialState } from "../reducers/manageUsersReducer";
import { createAddUserRequest } from "../dto/userDto";
import SearchFilterBar from "../components/common/SearchFilterBar";
import CustomPagination from "../components/common/CustomPagination";
import { useTableColumns } from "../hooks/useTableColumns";

export default function ManageUsersPage() {
	const keycloakAuth = useAuth();
	const dispatch = useDispatch();
	const [t] = useTranslation();

	const { showToast,success,error:ToastError,warning,info, } = useToast();
	const selectedCompany = useSelector((state) => state.selectedCompany.data?.companyData);
	const companyRoles = useSelector((state) => state.selectedCompany.data?.companyRoles);

	const { data: companyUsersData, loading, error } = useSelector((state) => state.companyUsers?.data);
	const userRolesFetched = useSelector((state) => state.userAccessData.data?.userRoleIds);

	const user = keycloakAuth.user;
	const userId = user?.id;

	// Add state to track deleted record IDs
	const [deletedRecordIds, setDeletedRecordIds] = useState(new Set());

	// Use reducer for dialog and form state management
	const [state, dispatchState] = useReducer(manageUsersReducer, initialState);

	// Ref to track ongoing thunk requests
	const activeThunksRef = useRef([]);

	const displayData = useMemo(
		() => companyUsersData?.filter((record) => !deletedRecordIds.has(record.userId)) || [],
		[companyUsersData, deletedRecordIds] // Dependencies
	);
	useEffect(() => {
		dispatch(fetchCompanyUsersRequest({ companyId: selectedCompany.companyId }));
	}, [dispatch]);

	// Cleanup function to abort all active thunks on unmount
	useEffect(() => {
		return () => {
			// Abort all active thunks when component unmounts
			activeThunksRef.current.forEach((thunk) => {
				if (thunk && typeof thunk.abort === "function") {
					thunk.abort();
				}
			});
			activeThunksRef.current = [];
		};
	}, []);

	// #region SEARCH & PAGINATION
	const { setSearchQuery, filteredData } = useSearchFilter(displayData, ["fullName", "email", "idType", "idNumber", "createdDate"], null);

	// Local pagination
	const totalPages = Math.max(1, Math.ceil((filteredData?.length || 0) / state.table.recordsPerPage));

	const pagedData = useMemo(() => {
		const start = (state.table.currentPage - 1) * state.table.recordsPerPage;
		const end = start + state.table.recordsPerPage;
		return (filteredData || []).slice(start, end);
	}, [filteredData, state.table.currentPage, state.table.recordsPerPage]);

	// #endregion

	// #region HELPER FUNCTION

	const fetchCompanyRoles = useCallback(
		async (companyId) => {
			try {
				// Dispatch the thunk and store the promise with abort method
				const thunkPromise = dispatch(fetchCompanyRolesThunk({ companyId }));

				// Add to active thunks for cleanup on unmount
				activeThunksRef.current.push(thunkPromise);

				const companyRolesResponse = await thunkPromise;

				// Remove from active thunks after completion
				activeThunksRef.current = activeThunksRef.current.filter((t) => t !== thunkPromise);

				if (fetchCompanyRolesThunk.rejected.match(companyRolesResponse)) {
					logger.error("Error fetching company roles:", companyRolesResponse?.payload?.data);
					return null;
				}

				const companyRoles = companyRolesResponse?.payload?.data;
				//logger.info(companyRoles)
				return companyRoles;
			} catch (error) {
				logger.error("Error in fetchCompanyRoles:", error);
				return null;
			}
		},
		[dispatch]
	);

	const fetchUserRoles = useCallback(
		async (userId, companyId) => {
			try {
				// Dispatch the thunk and store the promise with abort method
				const thunkPromise = dispatch(getUserRolesThunk({ userId: userId, companyId: companyId }));

				// Add to active thunks for cleanup on unmount
				activeThunksRef.current.push(thunkPromise);

				const userRolesResponse = await thunkPromise;

				// Remove from active thunks after completion
				activeThunksRef.current = activeThunksRef.current.filter((t) => t !== thunkPromise);

				if (getUserRolesThunk.rejected.match(userRolesResponse)) {
					logger.error("Error fetching user roles:", userRolesResponse.payload);
					return null;
				}

				const userRoles = userRolesResponse.payload?.data;
				//logger.info(userRoles)
				return userRoles;
			} catch (error) {
				logger.error("Error in fetchUserRoles:", error);
				return null;
			}
		},
		[dispatch]
	);
	//#endregion

	// #region ASSIGN/MODIFY ROLES PROCESS
	const handleModifyRolesClick = useCallback(
		async (row) => {
			// Show backdrop
			dispatchState({ type: "SET_LOADING", payload: { key: "isLoadingBackdrop", value: true } });

			//Parallel API Call
			const initialRoles = await fetchUserRoles(row.userId, selectedCompany.companyId);
			//Parallel API Call
			/* const [initialRoles, companyRolesRecs] = await Promise.all([
			fetchUserRoles(row.userId, selectedCompany.companyId),  // Starts immediately
			fetchCompanyRoles(selectedCompany.companyId),          // Starts immediately (not waiting!)
		]); */

			// Hide backdrop after API calls complete
			dispatchState({ type: "SET_LOADING", payload: { key: "isLoadingBackdrop", value: false } });

			dispatchState({
				type: "OPEN_MODIFY_ROLES_DIALOG",
				payload: {
					record: row,
					roleOptions: companyRoles,
					initialRoles: initialRoles || [],
				},
			});
		},
		[selectedCompany.companyId, fetchCompanyRoles, fetchUserRoles]
	);

	const handleCancelModifyRoles = useCallback(() => {
		dispatchState({ type: "CLOSE_MODIFY_ROLES_DIALOG" });
	}, []);
	const handleConfirmModifyRoles = useCallback(async (changedFields, fullData) => {
		dispatchState({ type: "SET_LOADING", payload: { key: "isLoadingBackdrop", value: true } });

		// Check if Roles changed
		if (!changedFields.Roles) {
			logger.info("No role changes detected, skipping update");
			dispatchState({ type: "CLOSE_MODIFY_ROLES_DIALOG" });
			dispatchState({ type: "SET_LOADING", payload: { key: "isLoadingBackdrop", value: false } });
			return;
		}

		// Calculate what changed
		const initialRoles = state.forms.initialRoleFormData.Roles;
		const currentRoles = fullData.Roles;

		// Find added roles (in current but not in initial)
		const addedRoles = currentRoles.filter((role) => !initialRoles.includes(role));

		// Find removed roles (in initial but not in current)
		const removedRoles = initialRoles.filter((role) => !currentRoles.includes(role));
		const body = {};

		let updateUserRolesRes;

		// Log only if there are changes
		if (addedRoles.length > 0 || removedRoles.length > 0) {
			logger.info("Role changes detected:");
			logger.info("Initial roles:", initialRoles);
			if (addedRoles.length > 0) {
				logger.info("Added roles:", addedRoles);
				body.RolesToAdd = addedRoles;
			}
			if (removedRoles.length > 0) {
				logger.info("Removed roles:", removedRoles);
				body.RolesToRemove = removedRoles;
			}
			logger.info("Final roles:", currentRoles);
 
			// Create the change object for API
			const roleChanges = {
				addedRoles,
				removedRoles,
				finalRoles: currentRoles,
			};
			logger.info("Role changes object:", roleChanges);
			updateUserRolesRes = await dispatch(
				patchUserRolesThunk({
					body: {
						...body,
						userId: state.dialogs.selectedRecord.userId,
						companyId: selectedCompany.companyId,
					}
				})
			);
 
			if (patchUserRolesThunk.rejected.match(updateUserRolesRes)) {
				logger.error("Error updating user roles:", updateUserRolesRes.payload);
				dispatchState({ type: "SET_LOADING", payload: { key: "isLoadingBackdrop", value: false } });
				ToastError(updateUserRolesRes.payload, {severity:"error"});
				return null;
			}
		} else {
			logger.info("No role changes detected");
			logger.info("  Current roles:", currentRoles);
		}
		dispatchState({ type: "CLOSE_MODIFY_ROLES_DIALOG" });
		dispatchState({ type: "SET_LOADING", payload: { key: "isLoadingBackdrop", value: false } });
		if (updateUserRolesRes && updateUserRolesRes.payload?.queued) {
			success("Roles update saved offline — will sync when reconnected");
		} else {
			success("User roles has been updated successfully");
		}
	}, [state.forms.initialRoleFormData.Roles, state.forms.assignRoleFormData.Roles, state.dialogs.selectedRecord, dispatch, selectedCompany.companyId]);
	//#endregion

	// #region Add PROCESS
	const handleAddClick = async () => {
		dispatchState({
			type: "OPEN_ADD_DIALOG",
			payload: {
				roleOptions: companyRoles,
			},
		});
	};
	const handleConfirmAdd = useCallback(async (formData) => {
		dispatchState({ type: "SET_LOADING", payload: { key: "isLoadingBackdrop", value: true } });

		try {
			// Create the request body using the factory function
			const body = createAddUserRequest(formData);

			logger.info("Add User Request Body:", body);

			// Dispatch the addUserThunk with required parameters
			const result = await dispatch(
				addUserThunk({
					companyId: selectedCompany.companyId,
					keycloakCompanyId: selectedCompany.keycloakCompanyId,
					body: body,
				})
			);

			// Check if the request was successful
			if (addUserThunk.fulfilled.match(result)) {
				if (result.payload?.queued) {
					success("User added offline — will sync when reconnected");
				} else {
					logger.info("User added successfully:", result.payload);
					// Refresh the company users list
					dispatch(fetchCompanyUsersRequest({ companyId: selectedCompany.companyId }));
					// Show success toast
					success("User added successfully");
				}
				// Close the dialog
				dispatchState({ type: "CLOSE_ADD_DIALOG" });
			} else if (addUserThunk.rejected.match(result)) {
				// Handle rejected request (400, 500, network errors, etc.)
				logger.error("Failed to add user:", result.payload);
				const errorMessage = result.payload || "Failed to add user. Please try again.";
				dispatchState({ type: "SET_LOADING", payload: { key: "isLoadingBackdrop", value: false } });
				// Show error toast
				ToastError(errorMessage, {severity:"error"});
			}
		} catch (error) {
			// This catch handles unexpected errors (e.g., if dispatch itself fails)
			logger.error("Unexpected error in handleConfirmAdd:", error);
			ToastError("An unexpected error occurred", {severity:"error"});
		} finally {
			dispatchState({ type: "SET_LOADING", payload: { key: "isLoadingBackdrop", value: false } });
		}
	}, [state.forms.addUserForm, selectedCompany.companyId, selectedCompany.keycloakCompanyId, dispatch]);
	const handleCancelAdd = useCallback(() => {
		dispatchState({ type: "CLOSE_ADD_DIALOG" });
	}, []);
	//#endregion

	// #region EDIT PROCESS
	const handleEditClick = useCallback(
		async (row) => {
			//logger.info("row: ", row)
			let editUserInitialFormData = {
				firstName: row.firstName,
				lastName: row.lastName,
				email: row.email,
				mobileNumber: row.mobileNumber,
				IdType: row.idType,
				idNumber: row.idNumber,
			}
			dispatchState({
				type: "OPEN_EDIT_DIALOG",
				payload: {
					record: row,
					editFormData: editUserInitialFormData,
					initialEditUserForm: editUserInitialFormData
				},
			});
		},
		[]
	);
	const handleCancelEdit = useCallback(() => {
		dispatchState({ type: "CLOSE_EDIT_DIALOG" });
	}, []);

	const handleConfirmEdit = useCallback((changedFields, fullData) => {
		// changedFields: Only the fields that were modified
		// fullData: All form data (for reference if needed)

		logger.info("Changed fields only: ", changedFields);
		logger.info("Full form data: ", fullData);

		// Check if there are any changes
		if (Object.keys(changedFields).length === 0) {
			logger.info("No changes detected, skipping update");
			dispatchState({ type: "CLOSE_EDIT_DIALOG" });
			return;
		}

		if (state.dialogs.selectedRecord) {
			logger.info("Selected record: ", state.dialogs.selectedRecord);
			// Pass only changed fields to the edit handler
			handleEdit(state.dialogs.selectedRecord.userId, changedFields);
		}
	}, [state.dialogs.selectedRecord]);


	const handleEdit = async (userId, changes) => {
		dispatchState({ type: "SET_LOADING", payload: { key: "isLoadingBackdrop", value: true } });
		try {
			logger.info("userID: ", userId)
			logger.info("changes: ", changes)
			// Dispatch the addUserThunk with required parameters
			const result = await dispatch(
				editUserThunk({
					userId: userId,
					body: changes,
					companyId: selectedCompany.companyId,
				})
			);

			if (editUserThunk.fulfilled.match(result)) {
				if (result.payload?.queued) {
					success("Saved offline — will sync when reconnected");
				} else {
					logger.info("User edited successfully:", result.payload);
					success("User edited successfully");
					// fetchCompanyUsersRequest is dispatched inside editUserThunk on success
				}
				dispatchState({ type: "CLOSE_EDIT_DIALOG" });
			} else if (editUserThunk.rejected.match(result)) {
				// Handle rejected request (400, 500, network errors, etc.)
				logger.error("Failed to edit user:", result.payload);
				const errorMessage = result.payload || "Failed to edit user. Please try again.";
				dispatchState({ type: "SET_LOADING", payload: { key: "isLoadingBackdrop", value: false } });
				// Show error toast
				ToastError(errorMessage, {severity:"error"});
			}
		} catch (error) {
			// This catch handles unexpected errors (e.g., if dispatch itself fails)
			logger.error("Unexpected error in handleConfirmEdit:", error);
			ToastError("An unexpected error occurred", {severity:"error"});
			
		} finally {
			dispatchState({ type: "SET_LOADING", payload: { key: "isLoadingBackdrop", value: false } });
		}
	};
	//#endregion

	// #region DELETE PROCESS
	const handleDeleteClick = useCallback((row) => {
		dispatchState({
			type: "OPEN_DELETE_DIALOG",
			payload: { ...row },
		});
	}, []);

	const handleCancelDelete = useCallback(() => {
		dispatchState({ type: "CLOSE_DELETE_DIALOG" });
	}, []);

	const handleConfirmDelete = useCallback(() => {
		if (state.dialogs.selectedRecord) {
			logger.info("state.dialogs.selectedRecord: ", state.dialogs.selectedRecord);
			handleRemove(state.dialogs.selectedRecord.userId);
		}
		dispatchState({ type: "CLOSE_DELETE_DIALOG" });
	}, [state.dialogs.selectedRecord]);

	const handleRemove = async (userId) => {
		try {
			// Dispatch the addUserThunk with required parameters
			const result = await dispatch(
				unassignUserThunk({
					userId: userId,
					companyId: selectedCompany.companyId,
					keycloakCompanyId: selectedCompany.keycloakCompanyId,
				})
			);

			if (unassignUserThunk.fulfilled.match(result)) {
				if (result.payload?.queued) {
					success("Will be unassigned when reconnected");
					dispatchState({ type: "CLOSE_ADD_DIALOG" });
				} else {
					logger.info("User unassigned successfully:", result.payload);
					dispatch(fetchCompanyUsersRequest({ companyId: selectedCompany.companyId }));
					dispatchState({ type: "CLOSE_ADD_DIALOG" });
					success("User unassigned successfully");
				}
			} else if (unassignUserThunk.rejected.match(result)) {
				// Handle rejected request (400, 500, network errors, etc.)
				logger.error("Failed to unassigned user:", result.payload);
				const errorMessage = result.payload || "Failed to unassigned user. Please try again.";
				// Show error toast
				ToastError(errorMessage, {severity:"error"});
			}
		} catch (error) {
			// This catch handles unexpected errors (e.g., if dispatch itself fails)
			logger.error("Unexpected error in handleConfirmAdd:", error);
			ToastError("An unexpected error occurred", {severity:"error"});
		} finally {
			dispatchState({ type: 'SET_LOADING', payload: { key: 'isLoadingBackdrop', value: false } });
		}
	};
	// #endregion

	// #region MANAGE USERS TABLE COLUMNS
	// Memoize actions to prevent unnecessary recalculations
	const actions = useMemo(() => ({
		onModifyRoles: handleModifyRolesClick,
		onDelete: handleDeleteClick,
		onEdit: handleEditClick,
	}), [handleModifyRolesClick, handleDeleteClick, handleEditClick]);

	// Context for conditional rendering (hide buttons for current user)
	const context = useMemo(() => ({
		currentUserId: userId,
	}), [userId]);

	// Use the dynamic columns hook
	const { columns, isLoading: columnsLoading } = useTableColumns('ManageUsers', {
		actions,
		context,
	});
	// #endregion

	// #region RETURN  COMPONENT

	/*     if (state.loading.isLoadingBackdrop) {
			return (
				<LoadingBackdrop
					open={state.loading.isLoadingBackdrop}
					message="Loading data, please wait..."
					variant="bars"
					color="greenGradient"
				/>
			);
		} */
	return (
		<Box sx={{ p: 3 }}>
			{/* Header */}
			<Typography variant="h3" gutterBottom color="primary">
				Manage Users Page
			</Typography>

			<Typography variant="body1" color="text.secondary" paragraph>
				This is a Manage Users page.
			</Typography>

			<Divider sx={{ my: 3 }} />
			<TableActionButton text="Add New User" variant="contained"  onClick={() => handleAddClick()} />
			{!companyUsersData || companyUsersData.length === 0 ? (
				<DataContainer
					data={[]}
					columns={columns} columnsLoading={columnsLoading}
					emptyStateIcon={CalendarIcon}
					emptyStateTitle="No users in this company"
					emptyStateSubtitle={`Start adding people to your company`}
				/>
			) : (
				<>
					<Box sx={{ p: 2 }}>
						<Box
							sx={{
								my: 2,
								display: 'flex',
								flexDirection: 'column',
								gap: 2,
							}}
						>
							<TableActionGroup
								sx={{
									display: "flex",
									justifyContent: "flex-end",

								}}
							>
							</TableActionGroup>
							<SearchFilterBar
								mode="frontend"
								searchValue={state.table.searchInput}
								onSearchChange={(v) => {
									dispatchState({ type: "SET_SEARCH_INPUT", payload: v });
									setSearchQuery(v);
								}}
								showFilterButton={false}
								showSearchButton={false}
								searchPlaceholder={t("Search users...")}
							/>
						</Box>

						<DataContainer data={pagedData} columns={columns} columnsLoading={columnsLoading} showRowNumbers maxHeight={600} stickyHeader />
					</Box>

					<Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
						<CustomPagination
							currentPage={state.table.currentPage}
							totalPages={totalPages}
							onPageChange={(e, v) => dispatchState({ type: "SET_CURRENT_PAGE", payload: v })}
							showFirstButton
							showLastButton
						/>
					</Box>
				</>
			)}
			<EnhancedConfirmationDialog
				open={state.dialogs.deleteConfirm}
				onClose={handleCancelDelete}
				onConfirm={handleConfirmDelete}
				title="Confirm Delete"
				message="Are you sure you want to delete this user from your organization? This action cannot be undone."
				confirmText="DELETE"
				confirmColor="error"
				cancelText="Cancel"
			/>
			<EnhancedConfirmationDialog
				open={state.dialogs.editConfirm}
				onClose={handleCancelEdit}
				onConfirm={handleConfirmEdit}
				title="Confirm Edit"
				confirmText="Edit"
				cancelText="Cancel"
				showMessage={false}
				returnChangedFieldsOnly={true}
				defaultValues={state.forms.editUserForm}
				fields={[
					{
						name: "firstName",
						label: "First Name",
						type: "text",
						required: true,
						placeholder: "Enter your first name",
					},
					{
						name: "lastName",
						label: "Last Name",
						type: "text",
						required: true,
						placeholder: "Enter your last name",
					},
					{
						name: "email",
						label: "Email",
						type: "text",
						regex: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
						regexErrorMessage: "Please enter a valid email address",
						placeholder: "Enter your email address",
					},
					{
						name: "mobileNumber",
						label: "Mobile Number",
						type: "text",
						required: true,
						placeholder: "Enter your mobile number (e.g., 0512345678)",
						regex: /^05\d{8}$/,
						regexErrorMessage: "Mobile number must start with 05 and be exactly 10 digits",
						inputMode: "numeric",
						pattern: "[0-9]*",
						numericOnly: true,
					},
					{
						name: "IdType",
						label: "Select ID Type",
						type: "text",
						disabled: true,
					},
					{
						name: "idNumber",
						label: "ID Number",
						type: "text",
						disabled: true,
					},
				]}
			/>
			<EnhancedConfirmationDialog
				open={state.dialogs.modifyRolesConfirm}
				onClose={handleCancelModifyRoles}
				onConfirm={handleConfirmModifyRoles}
				title="Modify User Role"
				message="Choose or Modify User Role"
				cancelText="Cancel"
				confirmText="Modify"
				confirmColor="primary"
				showMessage={false}
				returnChangedFieldsOnly={true}
				defaultValues={state.forms.assignRoleFormData}
				fields={[
					{
						name: "Roles",
						label: "Choose or modify user role",
						type: "dropdown",
						required: true,
						multiple: true,
						errorMessage: "Please select at least one role",
						options: state.ui.roleOptions,
						placeholder: "Select roles...",
					},
				]}
			/>

			<EnhancedConfirmationDialog
				open={state.dialogs.addConfirm}
				onClose={handleCancelAdd}
				onConfirm={handleConfirmAdd}
				title="Add New User"
				message="Add user to your comapny"
				cancelText="Cancel"
				confirmText="Confirm"
				confirmColor="primary"
				showMessage={false}
				defaultValues={state.forms.addUserForm}
				fields={[
					{
						name: "firstName",
						label: "First Name",
						type: "text",
						required: true,
						placeholder: "Enter your first name",
					},
					{
						name: "lastName",
						label: "Last Name",
						type: "text",
						required: true,
						placeholder: "Enter your last name",
					},
					{
						name: "email",
						label: "Email",
						type: "text",
						regex: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
						regexErrorMessage: "Please enter a valid email address",
						placeholder: "Enter your email address",
					},
					{
						name: "mobileNumber",
						label: "Mobile Number",
						type: "text",
						required: true,
						placeholder: "Enter your mobile number (e.g., 0512345678)",
						regex: /^05\d{8}$/,
						regexErrorMessage: "Mobile number must start with 05 and be exactly 10 digits",
						inputMode: "numeric",
						pattern: "[0-9]*",
						numericOnly: true,
					},
					{
						name: "IdType",
						label: "Select ID Type",
						type: "select",
						required: true,
						options: [
							{ label: "Iqama", value: "Iqama" },
							{ label: "NationaId", value: "Nationa ID" },
						],
					},
					{
						name: "idNumber",
						label: "ID Number",
						type: "text",
						required: true,
						placeholder: "Enter ID Number (10 digits)",
						regex: /^\d{10}$/,
						regexErrorMessage: "ID Number must be exactly 10 digits",
						inputMode: "numeric",
						pattern: "[0-9]*",
						numericOnly: true,
					},
					{
						name: "Roles",
						label: "Choose user's roles",
						type: "dropdown",
						required: true,
						multiple: true,
						errorMessage: "Please select at least one role",
						options: state.ui.roleOptions,
						placeholder: "Select roles...",
					},
				]}
			/>
			<LoadingBackdrop open={state.loading.isLoadingBackdrop} message="Loading data, please wait..." variant="bars" color="greenGradient" />
		</Box>
	);
	// #endregion
}
